/* ===========================================================================
 *  Scannen: het reken- en knipwerk
 *
 *  LETTERLIJK overgenomen uit src/lib/bonscannen.ts van het dashboard: het
 *  stuk tussen "De emmer" en "5. Het wegschrijven". Een pad, een
 *  verkleinde foto en een PDF van acht foto's horen er op de telefoon precies
 *  zo uit te zien als in het dashboard -- bon-scannen rekent hetzelfde pad
 *  na. dev/kopie-nakijken.mjs legt de twee naast elkaar bij elke build.
 *
 *  Niet hier veranderen. Verandert het daar, dan hier opnieuw kopiëren.
 * =========================================================================== */

/**
 * De emmer, en het is met opzet dezelfde als die van de post.
 *
 * Een gescande factuur is in alles wat telt hetzelfde stuk als een gemailde:
 * hij wordt gelezen, nagekeken, afgetekend en met bijlage naar Exact
 * gestuurd. Een eigen emmer zou betekenen dat elke leesregel, elke
 * ondertekende link en elke opruiming twee keer geschreven moet worden -- en
 * dat de tweede op een dag net iets anders doet dan de eerste.
 */
export const EMMER = 'post'

/**
 * Waar een gescand stuk in de emmer terechtkomt.
 *
 * Een eigen tak naast de mailberichten (die staan onder <berichtid>/). Zo is
 * met één blik in de opslag te zien wat er gescand is en wat er gemaild, en
 * kan een storageregel ooit op dit voorvoegsel worden gezet zonder dat er
 * iets verplaatst hoeft te worden.
 */
export const MAP = 'scan'

/**
 * De bovengrens van de emmer 'post': 26214400 bytes (setup.sql).
 *
 * Hier overgenomen en niet opgezocht, want het scherm moet het kunnen zeggen
 * vóór het uploaden. Wijkt dit ooit af van de emmer, dan weigert de opslag
 * het alsnog -- de controle hier is er om het eerder en in gewone woorden te
 * zeggen, niet om de echte grens te vervangen.
 */
export const MAX_BESTAND = 26214400

/**
 * Wat je kunt aanbieden.
 *
 * Een deelverzameling van wat de emmer toestaat: daar staan ook XML, CSV en
 * Excel in, want die komen per mail binnen. Met een telefoon maak je een
 * foto of kies je een PDF, en alles daarbuiten is een vergissing die beter
 * meteen opvalt.
 *
 * image/heic staat erbij omdat een iPhone dat standaard maakt. Hij wordt
 * hieronder sowieso omgezet naar JPEG -- maar als het omzetten niet lukt
 * (een browser die HEIC niet kan tekenen) moet het ruwe bestand er alsnog
 * in kunnen.
 */
export const TOEGESTAAN = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
]

/**
 * Wat de bestandskiezer op de telefoon aanbiedt.
 *
 * Uit dezelfde lijst, en dat is het punt: de kiezer biedt precies aan wat
 * keurBestand() hieronder ook aanneemt. Stonden er twee lijsten, dan kan de
 * kiezer iets aanbieden dat een regel verderop alsnog wordt geweigerd -- en
 * dat is de vervelendste soort weigering, want je hebt de foto al gemaakt.
 *
 * Hier stond "image/<ster>,application/pdf", wat korter is en hetzelfde
 * doet. Twee redenen om het niet te doen. Het laat soorten door die we
 * daarna toch weigeren (image/tiff, image/svg+xml), en de ster achter een
 * schuine streep is in een TypeScript-bestand het begin van een
 * commentaarblok -- zonderCommentaar() in de zelftest eet dan de rest van
 * het bestand op en elke controle erna meet niets.
 */
export const ACCEPT = TOEGESTAAN.join(',')

/**
 * Hoeveel bladzijden er aan één factuur mogen hangen.
 *
 * Geen technische grens maar een menselijke: wie twintig foto's bij één bon
 * stopt is geen factuur aan het scannen. En het telt: twintig bladzijden van
 * 197 kB is bijna 4 MB in één PDF, over een telefoonverbinding.
 */
export const MAX_PAGINAS = 12

/** En hoeveel losse facturen er in één keer mogen. Zelfde gedachte. */
export const MAX_BONNEN = 10

export type Richting = 'inkoop' | 'verkoop'

/* ------------------------------------------------------------------ *
 *  1. Wat er mis kan zijn aan een bestand
 * ------------------------------------------------------------------ */

export type Klacht =
  | { soort: 'te-groot'; max: number; had: number }
  | { soort: 'leeg' }
  | { soort: 'soort'; mime: string }

/**
 * Nakijken wat er binnenkomt, vóór het uploaden.
 *
 * Een lege map waar een `size` van 0 uitkomt is geen theoretisch geval: dat
 * is wat je krijgt als iemand in de bestandskiezer van Android een map
 * aanwijst in plaats van een bestand. Zonder deze regel gaat er nul bytes
 * naar de opslag, komt er een bon van, en staat de AI-assistent naar een
 * leeg bestand te kijken.
 */
export function keurBestand(
  bestand: { name?: string; type?: string; size: number },
): Klacht | null {
  if (bestand.size <= 0) return { soort: 'leeg' }
  if (bestand.size > MAX_BESTAND) {
    return { soort: 'te-groot', max: MAX_BESTAND, had: bestand.size }
  }
  const mime = soortVanBestand(bestand)
  if (!TOEGESTAAN.includes(mime)) return { soort: 'soort', mime: bestand.type || 'onbekend' }
  return null
}

/**
 * Het soort van een bestand, en de bestandsnaam als vangnet.
 *
 * Android geeft bij "kies een bestand" regelmatig een leeg type terug, en
 * een enkele keer application/octet-stream. Alleen naar `type` kijken zou
 * een doodgewone factuur.pdf weigeren met "dit soort bestand nemen we niet
 * aan" -- een weigering die nergens op slaat en waar de gebruiker niets mee
 * kan.
 */
export function soortVanBestand(bestand: { name?: string; type?: string }): string {
  const mime = (bestand.type ?? '').trim().toLowerCase()
  if (mime && mime !== 'application/octet-stream') return mime

  const ext = (bestand.name ?? '').toLowerCase().match(/\.([a-z0-9]{1,5})$/)?.[1] ?? ''
  switch (ext) {
    case 'pdf': return 'application/pdf'
    case 'jpg': case 'jpeg': return 'image/jpeg'
    case 'png': return 'image/png'
    case 'webp': return 'image/webp'
    case 'heic': return 'image/heic'
    case 'heif': return 'image/heif'
    default: return mime || 'onbekend'
  }
}

/** De klacht in gewone woorden, voor op het scherm. */
export function klachtTekst(k: Klacht): string {
  switch (k.soort) {
    case 'te-groot':
      return `Dit bestand is ${Math.round(k.had / 1024 / 1024)} MB. `
        + `Maximaal ${Math.round(k.max / 1024 / 1024)} MB.`
    case 'leeg':
      return 'Dit bestand is leeg. Koos je per ongeluk een map in plaats van een bestand?'
    case 'soort':
      return `Alleen foto’s en PDF. Dit is ${k.mime}.`
  }
}

/** Is dit een foto (en dus te verkleinen en samen te voegen)? */
export function isFoto(bestand: { name?: string; type?: string }): boolean {
  return soortVanBestand(bestand).startsWith('image/')
}

/* ------------------------------------------------------------------ *
 *  2. Namen en paden
 * ------------------------------------------------------------------ */

/**
 * De id van een gescande bon.
 *
 * Het voorvoegsel zegt waar hij vandaan komt, net als exp_mail_ bij de post.
 * Daarachter zestien hex-tekens uit randomUUID.
 *
 * Waarom de TELEFOON hem maakt en niet de server: dit is wat een tweede
 * poging veilig maakt. Valt de verbinding weg nadat de server de bon heeft
 * aangemaakt maar vóór het antwoord terug is, dan drukt de gebruiker
 * opnieuw -- met dezelfde id. De server loopt dan op een botsing (23505) en
 * meldt "die stond er al" in plaats van een tweede bon voor dezelfde
 * factuur. Zelfde truc als bonId() in _gedeeld/bonuitmail.ts, die om precies
 * deze reden van het berichtnummer wordt afgeleid.
 *
 * Zestien tekens en niet twaalf: bij een botsing zou de tweede scan
 * stilletjes verdwijnen onder "stond er al", en dat is het ergste soort
 * fout. 64 bits maakt dat onmogelijk in plaats van onwaarschijnlijk.
 */
export function scanBonId(): string {
  const ruw = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '')
    : (Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2))
  return 'exp_scan_' + ruw.replace(/[^0-9a-f]/g, '').slice(0, 16).padEnd(16, '0')
}

/**
 * Is dit een id die een scan mag uitdelen?
 *
 * De server kijkt hier ook naar, en dat is het punt: de id komt van een
 * telefoon, dus van buiten. Zonder deze vorm zou een verzoek `exp_mail_...`
 * kunnen noemen en daarmee over een bon uit de post heen schrijven -- of
 * erger, er eentje bezet houden die nog moest binnenkomen.
 */
export function geldigeBonId(id: string): boolean {
  return /^exp_scan_[0-9a-f]{16}$/.test(id)
}

/**
 * Een bestandsnaam die in een opslagpad mag staan.
 *
 * Supabase Storage neemt een heleboel aan, maar niet alles, en een naam die
 * geweigerd wordt levert een foutmelding op die over het pad gaat in plaats
 * van over het bestand. Accenten eraf, de rest op streepjes, en kort houden.
 */
export function veiligeNaam(naam: string): string {
  const kaal = (naam || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.-]+/, '')
    .slice(-80)
  return kaal || 'scan'
}

/**
 * Mag deze id een map in de emmer zijn?
 *
 * De id van de beller komt uit profiles, dus uit de database en niet van een
 * toestel. Toch staat hier een controle, want profiles.id is sinds 0007
 * gewoon `text`: er kan in principe van alles in staan, en een schuine
 * streep erin zou betekenen dat een scan ergens anders in de emmer landt.
 *
 * Weigeren en niet schoonpoetsen. Wie hem door veiligeNaam() haalt, maakt
 * van "a/b" en "a-b" dezelfde map -- en dan schrijven twee mensen in elkaars
 * map, precies wat deze map moest voorkomen.
 */
export function geldigeMapnaam(id: string): boolean {
  return /^[A-Za-z0-9_-]{1,64}$/.test(id)
}

/**
 * Waar een gescand stuk komt te staan.
 *
 * scan/<wie>/<bonid>/<volgnummer>-<naam>.
 *
 * De BELLER vooraan, en dat is het slot op deze emmer. Zonder hem bepaalde
 * de bon-id alleen waar er geschreven mocht worden -- en die id maakt de
 * telefoon, dus de server kon er alleen de VORM van nakijken. Elke ingelogde
 * medewerker kon zo een uploadplek vragen voor het scan-id van een ander en
 * er met upsert overheen schrijven. Kostenpost-id's staan gewoon in beeld in
 * de app, dus zo'n id is voor een collega niet geheim; wat je daarmee kon
 * doen is het document onder een handtekening vervangen nadat iemand het had
 * gezien. In deze emmer ligt bovendien alle binnengekomen post.
 *
 * Nu kan niemand buiten zijn eigen map schrijven, wat voor id hij ook
 * verzint, en betekent `upsert: true` alleen nog "mijn eigen vorige poging
 * overschrijven".
 *
 * De bon-id daarachter, zodat alles van één factuur bij elkaar staat en er
 * bij het opruimen niets hoeft te worden uitgezocht. Het volgnummer ervoor,
 * zodat twee foto's die allebei "image.jpg" heten (en dat heten ze) elkaar
 * niet overschrijven.
 *
 * Geen jaar en maand zoals bij documenten.ts: daar is de emmer een kast die
 * mensen doorbladeren, hier zijn de beller en de bon-id al de ordening.
 *
 * Deze functie wordt in de app zelf niet aangeroepen -- het pad komt van de
 * server, met opzet. Hij staat hier omdat de serverfunctie niets uit src/
 * kan importeren en de twee berekeningen letterlijk gelijk moeten blijven;
 * scripts/selftest/scannen.ts legt ze naast elkaar.
 */
export function maakScanPad(
  bellerId: string, bonId: string, volgnummer: number, naam: string,
): string {
  return `${MAP}/${bellerId}/${bonId}/${volgnummer}-${veiligeNaam(naam)}`
}

/**
 * Wat er als omschrijving op de bon komt.
 *
 * Geen leveranciersnaam en geen bedrag: die weten we niet, en verzinnen is
 * erger dan leeg laten. Wel wanneer en hoe hij binnenkwam, want dat is het
 * enige wat we met zekerheid weten -- en het is precies wat iemand die de
 * rij doorloopt wil zien staan bij een bon van nul euro.
 *
 * De AI-assistent schrijft hier overheen zodra hij het stuk gelezen heeft.
 */
export function omschrijvingVoor(richting: Richting, nu: number, paginas: number): string {
  const d = new Date(nu)
  const datum = `${String(d.getDate()).padStart(2, '0')}-`
    + `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
  const kant = richting === 'verkoop' ? 'Verkoopfactuur' : 'Inkoopfactuur'
  const blad = paginas > 1 ? ` · ${paginas} bladzijden` : ''
  return `${kant} gescand op ${datum}${blad}`
}

/* ------------------------------------------------------------------ *
 *  3. Een telefoonfoto kleiner maken
 *
 *  Gemeten, met een echte browser en een echte JPEG-encoder, op een
 *  nagebouwde foto van een factuur op 4032x3024 (12 MP, de gewone stand van
 *  een telefoon) met sensorruis erin:
 *
 *      origineel  4032x3024  q0.92   3061 kB
 *      max 2600   2600x1950  q0.80    462 kB
 *      max 2200   2200x1650  q0.78    300 kB
 *      max 2000   2000x1500  q0.75    216 kB
 *      max 2000   2000x1500  q0.72    197 kB   <- dit
 *      max 1600   1600x1200  q0.72    128 kB
 *
 *  Drie megabyte per bladzijde is geen ramp voor de emmer (die neemt 25 MB
 *  aan) maar wel voor de gebruiker: zes bladzijden is 18 MB over een
 *  mobiele verbinding, en dat is het verschil tussen "even scannen" en "dat
 *  doe ik straks wel op kantoor". 197 kB is een factor vijftien.
 *
 *  Waarom 2000 pixels en niet minder: een A4 op 2000 pixels in de lengte is
 *  ongeveer 170 dpi. Dat is ruim boven wat nodig is om een bedrag en een
 *  factuurnummer te lezen, en ver genoeg boven de ondergrens dat een foto
 *  die scheef of onscherp is nog steeds leesbaar blijft. Bij 1600 bespaar je
 *  70 kB en lever je de marge in die je bij een slechte foto nodig hebt --
 *  en een onleesbare scan kost een mens tien minuten, 70 kB kost niemand
 *  iets.
 * ------------------------------------------------------------------ */

export const MAX_ZIJDE = 2000
export const KWALITEIT = 0.72

/**
 * Vanaf wanneer het verkleinen de moeite waard is.
 *
 * Onder de 400 kB is een foto al klein genoeg, en dan is opnieuw
 * comprimeren alleen maar verlies: je haalt er een generatie JPEG-artefacten
 * bij zonder dat er iets te winnen valt.
 */
export const VERKLEIN_VANAF = 400 * 1024

/** Moet deze foto kleiner voordat hij de deur uit gaat? */
export function moetVerkleind(bestand: { name?: string; type?: string; size: number }): boolean {
  return isFoto(bestand) && bestand.size > VERKLEIN_VANAF
}

/**
 * De maat waarop een foto uitkomt.
 *
 * Apart en puur, zodat de zelftest kan narekenen dat de verhouding bewaard
 * blijft. Een foto die wordt uitgerekt is een foto waarop de AI-assistent
 * zijn tekstherkenning op scheve letters mag doen.
 */
export function nieuweMaat(
  breed: number, hoog: number, maxZijde = MAX_ZIJDE,
): { breed: number; hoog: number } {
  const langste = Math.max(breed, hoog)
  if (langste <= maxZijde || langste <= 0) return { breed, hoog }
  const schaal = maxZijde / langste
  return {
    breed: Math.max(1, Math.round(breed * schaal)),
    hoog: Math.max(1, Math.round(hoog * schaal)),
  }
}

/**
 * Een foto verkleinen in de browser.
 *
 * Lukt het niet -- een HEIC die deze browser niet kan tekenen, een canvas
 * die geen blob teruggeeft -- dan komt het ORIGINEEL terug en niet een
 * fout. Een scan die afketst omdat het verkleinen niet lukte is een scan die
 * niet gemaakt is; drie megabyte uploaden is traag maar werkt.
 */
export async function verkleinFoto(bestand: File): Promise<File> {
  if (!moetVerkleind(bestand)) return bestand
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return bestand

  try {
    const bitmap = await createImageBitmap(bestand)
    const { breed, hoog } = nieuweMaat(bitmap.width, bitmap.height)

    const doek = document.createElement('canvas')
    doek.width = breed
    doek.height = hoog
    const tekenaar = doek.getContext('2d')
    if (!tekenaar) { bitmap.close(); return bestand }

    tekenaar.imageSmoothingEnabled = true
    tekenaar.imageSmoothingQuality = 'high'
    tekenaar.drawImage(bitmap, 0, 0, breed, hoog)
    bitmap.close()

    const blob = await new Promise<Blob | null>((ok) =>
      doek.toBlob((b) => ok(b), 'image/jpeg', KWALITEIT))
    if (!blob) return bestand

    /* Groter geworden? Dan het origineel houden. Dat kan echt gebeuren bij
       een kleine PNG met weinig kleuren: JPEG is daar slechter in. */
    if (blob.size >= bestand.size) return bestand

    const naam = bestand.name.replace(/\.[a-z0-9]+$/i, '') + '.jpg'
    return new File([blob], naam, { type: 'image/jpeg', lastModified: Date.now() })
  } catch {
    return bestand
  }
}

/* ------------------------------------------------------------------ *
 *  4. Van losse foto's één PDF
 *
 *  Een kostenpost heeft één attachment_path (setup.sql) en dat is geen
 *  tekortkoming maar de kern: dat ene bestand gaat straks als bijlage mee
 *  naar Exact. Twee bladzijden van één factuur als twee bonnen neerzetten
 *  zou twee keer hetzelfde bedrag in de rij zetten -- precies de fout die de
 *  administratie het duurst komt te staan.
 *
 *  Dus worden de bladzijden één PDF. Met de hand, net als pdfmaken.ts
 *  ernaast, en om dezelfde reden: jsPDF en pdf-lib zijn allebei goed en
 *  allebei honderden kilobytes die dan op élke telefoon worden gedownload.
 *  Wat hier nodig is, is klein en scherp begrensd -- de JPEG-bytes gaan er
 *  ONGEWIJZIGD in (/DCTDecode), dus er wordt niets opnieuw gecomprimeerd en
 *  er is geen pixel aan te pas gekomen.
 * ------------------------------------------------------------------ */

export interface PdfBlad {
  /** De ruwe JPEG-bytes. Niet opnieuw coderen: ze gaan er zo in. */
  jpeg: Uint8Array
  breed: number
  hoog: number
}

/**
 * De maten van een JPEG uit zijn eigen kop lezen.
 *
 * Nodig omdat een PDF moet weten hoe groot het plaatje is, en we het beeld
 * hier juist NIET willen openen -- dat zou betekenen dat we het decoderen en
 * opnieuw coderen, met kwaliteitsverlies dat niemand gevraagd heeft.
 *
 * Een JPEG is een rij blokken: 0xFF, een merk, twee bytes lengte, inhoud. De
 * maten staan in een SOF-blok (C0 t/m CF, behalve C4, C8 en CC -- dat zijn
 * tabellen en geen kader). Geeft null terug als het geen JPEG blijkt; dan
 * valt de beller terug op het bestand zelf.
 */
export function jpegMaten(bytes: Uint8Array): { breed: number; hoog: number } | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null

  let i = 2
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) { i++; continue }
    const merk = bytes[i + 1]
    /* Vulbytes en losse merken zonder lengte. */
    if (merk === 0xff || merk === 0x01 || (merk >= 0xd0 && merk <= 0xd9)) { i += 2; continue }

    const lengte = (bytes[i + 2] << 8) | bytes[i + 3]
    if (lengte < 2) return null

    const isKader = merk >= 0xc0 && merk <= 0xcf
      && merk !== 0xc4 && merk !== 0xc8 && merk !== 0xcc
    if (isKader) {
      /* In een SOF-blok staat de HOOGTE voor de breedte. Hier komt hij er
         andersom uit, in dezelfde volgorde als nieuweMaat() en PdfBlad --
         twee vormen die op elkaar lijken en net anders zijn, is precies hoe
         een foto gedraaid in een PDF belandt. */
      return {
        breed: (bytes[i + 7] << 8) | bytes[i + 8],
        hoog: (bytes[i + 5] << 8) | bytes[i + 6],
      }
    }
    i += 2 + lengte
  }
  return null
}

/** A4 in punten, zoals pdfmaken.ts het ook aanhoudt. */
const A4_BREED = 595
const A4_HOOG = 842

/**
 * Waar een plaatje op een A4 komt te staan.
 *
 * Passend binnen de bladspiegel, met de verhouding intact en gecentreerd.
 * Een liggende foto wordt dus niet gedraaid maar kleiner -- draaien zou
 * betekenen dat de AI-assistent een kwartslag gedraaide tekst krijgt bij een
 * foto die staand bedoeld was, en dat raad je niet goed aan de verhouding
 * alleen.
 */
export function plaatsOpBlad(
  breed: number, hoog: number,
): { x: number; y: number; breed: number; hoog: number } {
  const marge = 18
  const ruimteB = A4_BREED - 2 * marge
  const ruimteH = A4_HOOG - 2 * marge
  const schaal = Math.min(ruimteB / (breed || 1), ruimteH / (hoog || 1))
  const b = (breed || 1) * schaal
  const h = (hoog || 1) * schaal
  return { x: (A4_BREED - b) / 2, y: (A4_HOOG - h) / 2, breed: b, hoog: h }
}

/**
 * Een PDF met één JPEG per bladzijde.
 *
 * Opbouw als pdfmaken.ts: object 1 is de catalogus, 2 de paginaboom, daarna
 * per blad drie objecten (de pagina, zijn inhoudsstroom en het plaatje).
 *
 * Het verschil met pdfmaken.ts zit in de bytes. Daar wordt alles als tekst
 * opgebouwd en op het eind naar Latin-1 vertaald; dat kan hier niet, want
 * een JPEG bevat elke byte-waarde die er bestaat en zou onderweg veranderen.
 * Dus wordt er hier in Uint8Arrays geteld -- en dat is precies waar de
 * xref-tabel op moet kloppen.
 */
export function maakFotoPdf(bladen: PdfBlad[]): Uint8Array {
  const codeer = new TextEncoder()
  const stukken: Uint8Array[] = []
  let lengte = 0
  const schrijf = (x: string | Uint8Array) => {
    const b = typeof x === 'string' ? codeer.encode(x) : x
    stukken.push(b)
    lengte += b.length
  }

  /* Per blad: de pagina, de stroom, het plaatje. Vaste volgorde, zodat de
     verwijzingen hieronder vooruit uit te rekenen zijn. */
  const PER_BLAD = 3
  const EERSTE = 3
  const kids = bladen.map((_, i) => `${EERSTE + i * PER_BLAD} 0 R`).join(' ')

  const plekken: number[] = []
  schrijf('%PDF-1.4\n')
  /*
   * Een regel met hoge bytes direct na de kop. Dat is geen versiering: hij
   * zegt tegen gereedschap dat dit bestand binair is, en zonder hem
   * verminkt een enkele mailserver of git-filter de JPEG-stromen.
   */
  schrijf(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]))

  const object = (nr: number, kop: string, stroom?: Uint8Array) => {
    plekken[nr - 1] = lengte
    schrijf(`${nr} 0 obj\n${kop}\n`)
    if (stroom) {
      schrijf('stream\n')
      schrijf(stroom)
      schrijf('\nendstream\n')
    }
    schrijf('endobj\n')
  }

  object(1, '<< /Type /Catalog /Pages 2 0 R >>')
  object(2, `<< /Type /Pages /Kids [${kids}] /Count ${bladen.length} >>`)

  bladen.forEach((blad, i) => {
    const paginaNr = EERSTE + i * PER_BLAD
    const stroomNr = paginaNr + 1
    const beeldNr = paginaNr + 2

    object(paginaNr,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_BREED} ${A4_HOOG}] `
      + `/Resources << /XObject << /Im0 ${beeldNr} 0 R >> >> `
      + `/Contents ${stroomNr} 0 R >>`)

    const p = plaatsOpBlad(blad.breed, blad.hoog)
    /* cm zet de plaatsing; een plaatje is in PDF altijd 1x1 op (0,0). */
    const inhoud = codeer.encode(
      `q\n${p.breed.toFixed(2)} 0 0 ${p.hoog.toFixed(2)} `
      + `${p.x.toFixed(2)} ${p.y.toFixed(2)} cm\n/Im0 Do\nQ\n`)
    object(stroomNr, `<< /Length ${inhoud.length} >>`, inhoud)

    object(beeldNr,
      `<< /Type /XObject /Subtype /Image /Width ${blad.breed} /Height ${blad.hoog} `
      + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode `
      + `/Length ${blad.jpeg.length} >>`, blad.jpeg)
  })

  const aantal = 2 + bladen.length * PER_BLAD
  const xref = lengte
  schrijf(`xref\n0 ${aantal + 1}\n0000000000 65535 f \n`)
  for (let n = 1; n <= aantal; n++) {
    schrijf(String(plekken[n - 1] ?? 0).padStart(10, '0') + ' 00000 n \n')
  }
  schrijf(`trailer\n<< /Size ${aantal + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`)

  const uit = new Uint8Array(lengte)
  let op = 0
  for (const s of stukken) { uit.set(s, op); op += s.length }
  return uit
}

/**
 * De bladzijden van één factuur tot één bestand maken.
 *
 * Eén foto blijft een foto -- die in een PDF wikkelen maakt hem alleen maar
 * groter en levert niets op, want de AI-assistent leest een plaatje net zo
 * goed als een PDF (zie _gedeeld/factuurlezer.ts, die er een image-blok van
 * maakt in plaats van een document-blok).
 *
 * Eén PDF blijft die PDF. Meer dan één bladzijde wordt een PDF.
 */
export async function bundelPaginas(
  bestanden: File[], bonId: string,
): Promise<File> {
  if (bestanden.length === 1) return bestanden[0]

  const bladen: PdfBlad[] = []
  for (const bestand of bestanden) {
    const bytes = new Uint8Array(await bestand.arrayBuffer())
    const maten = jpegMaten(bytes)
    /*
     * Geen JPEG? Dan kan hij er niet in. Dat kan alleen gebeuren als het
     * verkleinen hierboven is teruggevallen op het origineel (een PNG, een
     * HEIC die deze browser niet kan tekenen). Overslaan zou een bladzijde
     * laten verdwijnen zonder een woord -- dus liever hard stoppen, zodat
     * het scherm kan zeggen wat er aan de hand is.
     */
    if (!maten) {
      throw new Error(
        `"${bestand.name}" kon niet bij de andere bladzijden gevoegd worden. `
        + 'Maak van deze bladzijde een losse bon, of maak er een gewone foto van.')
    }
    bladen.push({ jpeg: bytes, breed: maten.breed, hoog: maten.hoog })
  }

  const pdf = maakFotoPdf(bladen)
  return new File([pdf as unknown as BlobPart], `${bonId}.pdf`,
    { type: 'application/pdf', lastModified: Date.now() })
}

